// iter148 — Admin-yönetilebilir hero slider (anasayfa karşılama)
// Ayarlar > Anasayfa CMS > Hero Slaytları'ndan yönetilir.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const DEFAULT_BG = "https://images.unsplash.com/photo-1590794056486-25dd6c04c3d7?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function HeroSlider({ brandName = "Bordo Seramik", tagline = "", brandPrimary = "#8D1B3D" }) {
  const [slides, setSlides] = useState(null);
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/shop/hero-slides");
        setSlides(Array.isArray(data) ? data : []);
      } catch { setSlides([]); }
    })();
  }, []);

  // Fallback (varsayılan) tek slayt: CMS'ten hiç slayt yoksa göster
  const list = (slides && slides.length > 0) ? slides : [{
    id: "default",
    title: tagline || "Yaşama değer katan detaylar",
    subtitle: "Doğru ürün, dürüst fiyat, güvenilir teslimat — banyodan mutfağa, dış cepheden peyzaja.",
    badge: brandName,
    image_url: DEFAULT_BG,
    cta_label: "Alışverişe Başla",
    cta_link: "/urunler",
    bg_color: "",
    text_color: "#ffffff",
    align: "left",
  }];

  // Auto-rotate
  useEffect(() => {
    if (list.length <= 1) return;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setIdx((i) => (i + 1) % list.length), 6000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [list.length]);

  if (slides === null) return <div className="h-[380px] bg-zinc-100 animate-pulse"/>;

  const s = list[idx % list.length];
  const align = s.align || "left";
  const bg = s.image_url
    ? { background: `linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 60%, transparent 100%), url(${s.image_url}) center/cover` }
    : { background: `linear-gradient(135deg, #18181b, ${s.bg_color || brandPrimary})` };
  const color = s.text_color || "#ffffff";

  const go = (n) => { setIdx((n + list.length) % list.length); };

  return (
    <section
      className="relative overflow-hidden text-white group"
      style={{ ...bg, minHeight: 380 }}
      data-testid="hero-slider"
    >
      <div className="relative max-w-7xl mx-auto px-4 py-14 md:py-20">
        <div className={`max-w-2xl ${align === "center" ? "mx-auto text-center" : ""}`}>
          {s.badge && (
            <div className="text-xs uppercase tracking-wider font-bold mb-3 opacity-90" style={{ color }}>
              {s.badge}
            </div>
          )}
          <h1 className="font-display font-bold text-3xl md:text-5xl lg:text-6xl leading-tight mb-4" style={{ color }} data-testid={`hero-title-${s.id}`}>
            {s.title || tagline || "Yaşama değer katan detaylar"}
          </h1>
          {s.subtitle && (
            <p className="text-sm md:text-lg mb-6 max-w-xl" style={{ color, opacity: 0.92, ...(align === "center" ? { marginLeft: "auto", marginRight: "auto" } : {}) }}>
              {s.subtitle}
            </p>
          )}
          {(s.cta_label && s.cta_link) && (
            <div className={`flex gap-3 flex-wrap ${align === "center" ? "justify-center" : ""}`}>
              <Link
                to={s.cta_link}
                data-testid={`hero-cta-${s.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white text-sm shadow-lg hover:scale-105 active:scale-95 transition-all"
                style={{ background: brandPrimary }}
              >
                {s.cta_label} <ArrowRight size={16}/>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Prev / Next */}
      {list.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(idx - 1)}
            data-testid="hero-prev"
            className="opacity-0 group-hover:opacity-100 transition-opacity absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center hover:bg-white/30"
            aria-label="Önceki">
            <ChevronLeft size={20}/>
          </button>
          <button
            type="button"
            onClick={() => go(idx + 1)}
            data-testid="hero-next"
            className="opacity-0 group-hover:opacity-100 transition-opacity absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center hover:bg-white/30"
            aria-label="Sonraki">
            <ChevronRight size={20}/>
          </button>
          {/* Dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="hero-dots">
            {list.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => go(i)}
                aria-label={`Slayt ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-white" : "w-1.5 bg-white/40 hover:bg-white/60"}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
